// Split a parenting timeblock around a short visit ("date night").
//
// Family-law use case: one parent has a long block (say Jane, Monday to
// Friday) and the other parent gets a short visit inside it (David,
// Wednesday 5pm to 7pm). Rather than overlapping events, the host block is
// split into up to three clean pieces:
//
//   Jane  Mon 00:00 → Wed 17:00   (transition: David picks up)
//   David Wed 17:00 → Wed 19:00   (the visit; transition: David drops off)
//   Jane  Wed 19:00 → Fri 15:00   (keeps the host block's own transition)
//
// Clean pieces keep the resolver, time-distribution math, prose summaries
// and calendar rendering all exact with no overlap special-casing.
//
// Handles hosts that wrap into the next week or across the cycle boundary;
// the pieces are re-homed into whichever week each one starts in.

import {
  DAYS_OF_WEEK,
  DAY_INDEX,
  MIN_PER_DAY,
  MIN_PER_WEEK,
  timeToMin,
  minToHHMM,
} from './weekMath.js'

// Flatten an event to absolute cycle minutes: { start, end }.
export function flattenEvent(e, wi) {
  const sOff = e.startWeekOffset || 0
  const eOff = e.endWeekOffset || 0
  const start = (wi + sOff) * MIN_PER_WEEK + (DAY_INDEX[e.startDay] ?? 0) * MIN_PER_DAY + timeToMin(e.startTime)
  let end = (wi + eOff) * MIN_PER_WEEK + (DAY_INDEX[e.endDay] ?? 0) * MIN_PER_DAY + timeToMin(e.endTime)
  if (end <= start) end += MIN_PER_WEEK // safety, matches rotation.js
  return { start, end }
}

// Inverse of flattenEvent for an interval: home week + day/time/offset fields
// (partner and transition are the caller's business). Start is normalized
// into the cycle; endWeekOffset is the number of week boundaries the
// interval crosses (0..N — a block may run up to a full rotation).
export function positionFromInterval(start, end, totalWeeks) {
  const cycle = totalWeeks * MIN_PER_WEEK
  const st = ((start % cycle) + cycle) % cycle
  const endAbs = st + (end - start)
  const wi = Math.floor(st / MIN_PER_WEEK)
  return {
    weekIndex: wi,
    startDay: DAYS_OF_WEEK[Math.floor((st % MIN_PER_WEEK) / MIN_PER_DAY)],
    startTime: minToHHMM(st),
    endDay: DAYS_OF_WEEK[Math.floor((endAbs % MIN_PER_WEEK) / MIN_PER_DAY)],
    endTime: minToHHMM(endAbs),
    startWeekOffset: 0,
    endWeekOffset: Math.floor(endAbs / MIN_PER_WEEK) - wi,
  }
}

// splitTimeblock(weeks, { weekIndex, eventIndex }, { weekIndex, event })
//   weeks — rotation.weeks (not mutated)
//   host  — which event gets split
//   visit — the carve-out, event-shaped, homed at visit.weekIndex
// → { ok: true, weeks } | { ok: false, reason }
export function splitTimeblock(weeks, host, visit) {
  const N = weeks?.length || 0
  const cycle = N * MIN_PER_WEEK
  const hostEvent = weeks?.[host?.weekIndex]?.events?.[host?.eventIndex]
  if (!hostEvent) return { ok: false, reason: 'Could not find the timeblock to split.' }

  const { start: S, end: E } = flattenEvent(hostEvent, host.weekIndex)
  let { start: s, end: e } = flattenEvent(visit.event, visit.weekIndex)

  // The host may wrap past the cycle end; slide the visit by whole cycles
  // until it lands inside the host's window.
  for (const k of [-1, 0, 1]) {
    if (s + k * cycle >= S && s + k * cycle < E) { s += k * cycle; e += k * cycle; break }
  }
  if (s < S || s >= E) {
    return { ok: false, reason: 'The visit must start inside the timeblock being split.' }
  }
  if (e > E) e = E // a visit reaching past the block's end just runs to the handoff
  if (s === S && e === E) {
    return { ok: false, reason: 'The visit covers the entire timeblock. Edit the block itself instead.' }
  }

  const pieces = []
  const make = (start, end, partner, transition, transitionDetails) => {
    const { weekIndex: wi, ...fields } = positionFromInterval(start, end, N)
    return { wi, event: { partner, ...fields, transition, transitionDetails } }
  }

  // Before the visit: host parent, handing off INTO the visit.
  if (s > S) pieces.push(make(S, s, hostEvent.partner, `${visit.event.partner}_picks`, ''))
  // The visit itself.
  pieces.push(make(s, e, visit.event.partner, visit.event.transition, visit.event.transitionDetails || ''))
  // After the visit: host parent, keeping the block's original handoff.
  if (e < E) pieces.push(make(e, E, hostEvent.partner, hostEvent.transition, hostEvent.transitionDetails))

  const newWeeks = weeks.map((w, i) => ({
    ...w,
    events: (w?.events || []).filter((_, j) => !(i === host.weekIndex && j === host.eventIndex)),
  }))
  for (const p of pieces) newWeeks[p.wi].events.push(p.event)
  return { ok: true, weeks: newWeeks }
}
